import React, { useState, useRef, useEffect } from 'react';
import { Monitor, ImageIcon, X, Loader2, Sparkles, Play, Download, Camera, Palette, Zap, Settings, RotateCcw, Save, Lock, ChevronDown } from 'lucide-react';
import { uploadToImgbb } from '../utils/imgbb';
import { uploadImageToS3, compressImage } from '../utils/s3Upload';
import { performVirtualTryOnV2 } from '../api/virtualTryon.api';
import { getUserSettings } from '../api';
import ProductSelectionModal from './ProductSelectionModal';
import MultiProductTypeSelector from './MultiProductTypeSelector';
import { PRODUCT_CATEGORIES_OPTIMIZED } from '../data/productCategoriesOptimized';
import EnhancedImagePreview from './EnhancedImagePreview';

const IMGBB_API_KEY = '8f4e5e8b4e8c12bce0844076eb332e1a';

// Full Screen Loader Component
const FullScreenLoader = ({ isVisible, progress, currentStep, videoGenerationEnabled = true, faceSwapEnabled = false, currentGenerationIndex = 0, totalGenerations = 1 }) => {
  const steps = videoGenerationEnabled && faceSwapEnabled ? [
    { icon: Camera, label: "Processing your photo", color: "from-purple-500 to-pink-500" },
    { icon: Palette, label: "Generating AI image", color: "from-blue-500 to-purple-500" },
    { icon: ImageIcon, label: "Performing face swap", color: "from-green-500 to-emerald-500" },
    { icon: Sparkles, label: "Creating video content", color: "from-indigo-500 to-blue-500" },
    { icon: Zap, label: "Finalizing content", color: "from-orange-500 to-red-500" }
  ] : videoGenerationEnabled ? [
    { icon: Camera, label: "Processing your photo", color: "from-purple-500 to-pink-500" },
    { icon: Palette, label: "Generating AI image", color: "from-blue-500 to-purple-500" },
    { icon: Sparkles, label: "Creating video content", color: "from-green-500 to-blue-500" },
    { icon: Zap, label: "Finalizing content", color: "from-orange-500 to-red-500" }
  ] : faceSwapEnabled ? [
    { icon: Camera, label: "Processing your photo", color: "from-purple-500 to-pink-500" },
    { icon: Palette, label: "Generating AI image", color: "from-blue-500 to-purple-500" },
    { icon: ImageIcon, label: "Performing face swap", color: "from-green-500 to-emerald-500" },
    { icon: Zap, label: "Finalizing content", color: "from-orange-500 to-red-500" }
  ] : [
    { icon: Camera, label: "Processing your photo", color: "from-purple-500 to-pink-500" },
    { icon: Palette, label: "Generating AI image", color: "from-blue-500 to-purple-500" },
    { icon: Zap, label: "Finalizing content", color: "from-orange-500 to-red-500" }
  ];

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-gradient-to-br from-purple-900 via-pink-900 to-purple-800 flex items-center justify-center">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 text-center text-white max-w-md mx-auto px-6">
        {/* Logo/Icon */}
        <div className="mb-8">
          <div className="w-24 h-24 bg-gradient-to-r from-white/20 to-white/10 backdrop-blur-sm rounded-3xl flex items-center justify-center mx-auto mb-4 border border-white/20">
            <Sparkles size={48} className="text-white animate-pulse" />
          </div>
          <h2 className="text-3xl font-bold mb-2 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
            Virtual Try-On
          </h2>
          <p className="text-purple-200 text-lg">
            {videoGenerationEnabled 
              ? 'Creating your personalized content'
              : 'Creating your personalized image content'
            }
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            {steps.map((step, index) => (
              <div key={index} className="flex flex-col items-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all duration-500 ${
                  index < currentStep 
                    ? `bg-gradient-to-r ${step.color} shadow-lg` 
                    : index === currentStep 
                    ? 'bg-white/20 backdrop-blur-sm border border-white/30 animate-pulse' 
                    : 'bg-white/10 backdrop-blur-sm border border-white/20'
                }`}>
                  <step.icon 
                    size={20} 
                    className={`${
                      index < currentStep ? 'text-white' : 
                      index === currentStep ? 'text-white animate-spin' : 
                      'text-white/50'
                    }`} 
                  />
                </div>
                <span className={`text-xs font-medium transition-all duration-500 ${
                  index <= currentStep ? 'text-white' : 'text-white/50'
                }`}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-white/20 rounded-full h-2 backdrop-blur-sm">
            <div 
              className="h-2 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Loading Animation */}
        <div className="flex justify-center space-x-2 mb-6">
          <div className="w-3 h-3 bg-white rounded-full animate-bounce"></div>
          <div className="w-3 h-3 bg-white rounded-full animate-bounce animation-delay-100"></div>
          <div className="w-3 h-3 bg-white rounded-full animate-bounce animation-delay-200"></div>
        </div>

        {/* Status Text */}
        <div className="text-purple-200">
          <p className="text-sm font-medium mb-1">
            {totalGenerations > 1 
              ? `Generating ${currentGenerationIndex} of ${totalGenerations}: ${steps[currentStep]?.label || "Processing..."}`
              : (steps[currentStep]?.label || "Processing...")
            }
          </p>
          <p className="text-xs opacity-75">
            {totalGenerations > 1 
              ? `Creating ${totalGenerations} virtual try-on${totalGenerations > 1 ? 's' : ''}. This may take ${totalGenerations * 2}-${totalGenerations * 3} minutes.`
              : (videoGenerationEnabled 
                ? 'This may take 2-3 minutes. Please don\'t close this window.'
                : 'This may take 1-2 minutes. Please don\'t close this window.'
              )
            }
          </p>
          <p className="text-xs mt-2 font-bold">
            {Math.round(progress)}% Complete
            {totalGenerations > 1 && (
              <span className="block mt-1">
                ({currentGenerationIndex}/{totalGenerations} generations)
              </span>
            )}
          </p>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-10 left-10 w-2 h-2 bg-white/30 rounded-full animate-ping"></div>
        <div className="absolute top-20 right-20 w-1 h-1 bg-pink-300 rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-1 h-1 bg-blue-300 rounded-full animate-pulse animation-delay-1000"></div>
        <div className="absolute bottom-10 right-10 w-2 h-2 bg-white/20 rounded-full animate-ping animation-delay-2000"></div>
      </div>
    </div>
  );
};

const VirtualTryView = ({ setActiveView, refreshKits, showNotification, refreshCredits, preSelectedProduct, showInsufficientCreditsModal, fourKEnabled = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [imgbbUrl, setImgbbUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [stream, setStream] = useState(null);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(preSelectedProduct || null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [generatedContents, setGeneratedContents] = useState([]); // For multiple results
  const [generationError, setGenerationError] = useState(null);
  const [loaderProgress, setLoaderProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentGenerationIndex, setCurrentGenerationIndex] = useState(0); // Track which generation is running
  const [cameraFacingMode, setCameraFacingMode] = useState('user'); // 'user' for front camera, 'environment' for back camera
  const [countdown, setCountdown] = useState(0);
  const [isCountingDown, setIsCountingDown] = useState(false);

  const [isFullScreenImage, setIsFullScreenImage] = useState(false);
  const [fullScreenImageUrl, setFullScreenImageUrl] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const countdownRef = useRef(null);
  const [isVirtualTryOnEnabled, setIsVirtualTryOnEnabled] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  
  // Product type and category selection states
  const [selectedProductCategory, setSelectedProductCategory] = useState('');
  const [selectedProductTypes, setSelectedProductTypes] = useState([]);
  const [showProductTypeSelection, setShowProductTypeSelection] = useState(false);
  const [showMultipleTypeTip, setShowMultipleTypeTip] = useState(false);

  // Set pre-selected product when component mounts or prop changes
  useEffect(() => {
    if (preSelectedProduct) {
      setSelectedProduct(preSelectedProduct);
      console.log('Pre-selected product set:', preSelectedProduct);
    }
  }, [preSelectedProduct]);

  // Auto-select product when photo is captured and product is pre-selected
  useEffect(() => {
    if (imgbbUrl && preSelectedProduct && !selectedProduct) {
      setSelectedProduct(preSelectedProduct);
      console.log('Auto-selecting pre-selected product after photo capture');
    }
  }, [imgbbUrl, preSelectedProduct, selectedProduct]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setIsCameraOpen(false);
    setCapturedImage(null);
    setImgbbUrl(null);
    setIsUploading(false);
    setStream(null);
    setSelectedProduct(null);
    setIsProductModalOpen(false);
    setGeneratedContent(null);
    setGenerationError(null);
    setLoaderProgress(0);
    setCurrentStep(0);
    setCameraFacingMode('user'); // Reset to front camera
    setCountdown(0);
    setIsCountingDown(false);

    setIsFullScreenImage(false);
    setFullScreenImageUrl(null);
  };

  const openFullScreenImage = (imageUrl) => {
    setFullScreenImageUrl(imageUrl);
    setIsFullScreenImage(true);
  };

  const closeFullScreenImage = () => {
    setIsFullScreenImage(false);
    setFullScreenImageUrl(null);
  };

  const openCamera = async () => {
    try {
      // Stop existing stream if any
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: cameraFacingMode,
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      });
      setStream(mediaStream);
      setIsCameraOpen(true);
    } catch (err) {
      console.error('Error accessing camera:', err);
      alert('Unable to access camera. Please check permissions.');
    }
  };

  const switchCamera = async () => {
    try {
      // Stop existing stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      
      // Toggle camera facing mode
      const newFacingMode = cameraFacingMode === 'user' ? 'environment' : 'user';
      setCameraFacingMode(newFacingMode);
      
      // Get new stream with different camera
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: newFacingMode,
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      });
      setStream(mediaStream);
    } catch (err) {
      console.error('Error switching camera:', err);
      alert('Unable to switch camera. Please try again.');
    }
  };

  // Ref callback to set srcObject only when video is mounted and ready
  const handleVideoRef = (node) => {
    if (node && isCameraOpen && stream) {
      node.srcObject = stream;
    }
    videoRef.current = node;
  };

  // Clean up stream on unmount or when stream changes
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      // Clean up countdown timer
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [stream]);

  const closeCamera = () => {
    setIsCameraOpen(false);
    setCapturedImage(null);
    setStream(null);
    // Clear countdown if active
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setCountdown(0);
    setIsCountingDown(false);
  };

  // Helper to convert dataURL to File
  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new File([u8arr], filename, { type: mime });
  };

  // Start countdown timer
  const startCountdown = () => {
    setCountdown(3);
    setIsCountingDown(true);
    
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          // Countdown finished, capture the image
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          setIsCountingDown(false);
          captureImage();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };



  // Capture and upload to S3
  const captureImage = async () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      const imageDataUrl = canvas.toDataURL('image/jpeg');
      setCapturedImage(imageDataUrl);
      closeCamera();
      setIsUploading(true);
      setImgbbUrl(null);
      setSelectedProduct(null);
      try {
        const file = dataURLtoFile(imageDataUrl, 'virtual-tryon-face.jpg');
        
        // Compress image before upload for better performance
        const compressedFile = await compressImage(file, {
          maxWidth: 800,
          maxHeight: 600,
          quality: 0.8
        });

        // Upload to S3
        const uploadResult = await uploadImageToS3(compressedFile, {
          folder: 'virtual-tryon',
          fileName: `virtual_tryon_face_${Date.now()}_${compressedFile.name}`,
          onProgress: (progress) => {
            console.log('Virtual try-on image upload progress:', progress);
          }
        });

        setImgbbUrl(uploadResult.url);
      } catch (err) {
        console.error('S3 upload error:', err);
        alert('Failed to upload image to S3.');
        setImgbbUrl(null);
      } finally {
        setIsUploading(false);
      }
    }
  };

  // Handle product selection
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setIsProductModalOpen(false);
    // Show product type and category selection after product is selected
    setShowProductTypeSelection(true);
    // Reset previous selections
    setSelectedProductCategory('');
    setSelectedProductTypes([]);
  };

  // Handle product category selection
  const handleCategoryChange = (categoryValue) => {
    setSelectedProductCategory(categoryValue);
    setSelectedProductTypes([]); // Reset types when category changes
  };

  // Handle multiple product types selection (for MultiProductTypeSelector)
  const handleMultipleTypesChange = (types) => {
    setSelectedProductTypes(types);
  };

  // Skip product type selection
  const handleSkipProductTypeSelection = () => {
    setShowProductTypeSelection(false);
  };

  // Handle TRY-ON generation with progress simulation
  const handleTryOn = async () => {
    if (!selectedProduct || !imgbbUrl) {
      alert('Please capture your face and select a product first.');
      return;
    }

    // Determine how many API calls we need to make
    const productTypesToGenerate = selectedProductTypes.length > 0 ? selectedProductTypes : [
      { value: selectedProduct.category || 'fabric', label: selectedProduct.category || 'Fabric', finalValue: selectedProduct.productType || 'fabric', finalLabel: selectedProduct.productType || 'Fabric' }
    ];
    
    const numberOfGenerations = productTypesToGenerate.length;

    // Check if user has enough image credits
    try {
      showNotification(`Checking available credits for ${numberOfGenerations} generation${numberOfGenerations > 1 ? 's' : ''}...`, "info");
      
      const { getUserProfile } = await import('../api');
      const token = localStorage.getItem('token');
      
      if (!token) {
        showNotification("Authentication required. Please log in again.", "error");
        return;
      }
      
      const response = await getUserProfile(token);
      
      if (response.data) {
        const userData = response.data.data.user;
        const availableCredits = userData.imageCredits || 0;
        const creditMultiplier = (userData.settings?.fourKEnabled ?? fourKEnabled) ? 2 : 1;
        const creditsNeeded = numberOfGenerations * creditMultiplier;
        
        console.log(`Credit check: Required ${creditsNeeded}, Available ${availableCredits}`);
        
        if (availableCredits < creditsNeeded) {
          showInsufficientCreditsModal(creditsNeeded, availableCredits, 'image');
          return;
        }
        
        console.log(`✅ Credit check passed: ${availableCredits} credits available for ${numberOfGenerations} virtual try-on${numberOfGenerations > 1 ? 's' : ''}`);
      } else {
        console.error('No user data received from API');
        showNotification("Unable to verify credits. Please try again.", "error");
        return;
      }
    } catch (error) {
      console.error('Error checking user credits:', error);
      showNotification("Unable to verify credits. Please try again.", "error");
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);
    setGeneratedContent(null);
    setGeneratedContents([]);
    setCurrentGenerationIndex(0);
    setLoaderProgress(0);
    setCurrentStep(0);

    // Progress simulation for multiple generations
    const progressInterval = setInterval(() => {
      setLoaderProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + Math.random() * 10;
      });
    }, 1000);

    // Step progression - account for face swap step
    const maxSteps = imgbbUrl ? 4 : 3; // 4 steps if face swap enabled, 3 if not
    const stepInterval = setInterval(() => {
      setCurrentStep(prev => {
        if (prev >= maxSteps) {
          clearInterval(stepInterval);
          return maxSteps;
        }
        return prev + 1;
      });
    }, 3000);

    try {
      const allResults = [];
      let successCount = 0;
      let errorCount = 0;

      // Make separate API calls for each product type
      for (let i = 0; i < numberOfGenerations; i++) {
        const productType = productTypesToGenerate[i];
        setCurrentGenerationIndex(i + 1);
        
        console.log(`🚀 Generating virtual try-on ${i + 1}/${numberOfGenerations} for: ${productType.finalLabel}`);
        
        if (numberOfGenerations > 1) {
          showNotification(`Generating virtual try-on ${i + 1} of ${numberOfGenerations}: ${productType.finalLabel}...`, "info");
        }

        try {
          const result = await performVirtualTryOnV2(
            productType.category || selectedProductCategory || productType.finalValue,
            productType.finalLabel || productType.label || productType.finalValue,
            selectedProduct.image || selectedProduct.images?.original?.imgbbUrl || selectedProduct.images?.original?.url,
            imgbbUrl,
            {
              ...(productType.customization && {
                productTypeCustomizations: productType.customization
              }),
              ...(productType.customizationReferences && {
                productTypeCustomizationReferences: productType.customizationReferences
              }),
              ...(productType.garmentInspirationImage && {
                garmentInspirationImage: productType.garmentInspirationImage
              })
            }
          );

          if (result.success) {
            allResults.push({
              ...result.data,
              productType: productType.finalLabel,
              productTypeValue: productType.finalValue,
              generationIndex: i + 1
            });
            successCount++;
            console.log(`✅ Virtual try-on ${i + 1} completed successfully`);
          } else {
            console.error(`❌ Virtual try-on ${i + 1} failed:`, result.message);
            errorCount++;
          }
        } catch (error) {
          console.error(`❌ Error generating virtual try-on ${i + 1}:`, error);
          errorCount++;
        }
      }

      clearInterval(progressInterval);
      clearInterval(stepInterval);
      setLoaderProgress(100);
      setCurrentStep(maxSteps);

      // Handle results
      if (successCount > 0) {
        setGeneratedContents(allResults);
        
        // For backward compatibility, set the first result as generatedContent
        if (allResults.length > 0) {
          setGeneratedContent(allResults[0]);
        }
        
        // Refresh credits after successful virtual try-ons
        if (refreshCredits) {
          refreshCredits();
        }
        
        // Show success notification
        if (numberOfGenerations > 1) {
          showNotification(`Virtual try-on completed! ${successCount} of ${numberOfGenerations} generations successful.`, 'success');
        } else {
          showNotification('Virtual try-on completed successfully!', 'success');
        }
        
        // Automatically navigate to My Kits after successful generation
        // Add a small delay to show the success state briefly
        setTimeout(async () => {
          try {
            // Refresh kits to get the latest data
            if (refreshKits) {
              await refreshKits();
            }
            
            // Navigate to My Kits page
            if (setActiveView) {
              setActiveView('mykits');
              if (showNotification && numberOfGenerations > 1) {
                showNotification(`Virtual try-on completed! Navigated to My Kits to view ${successCount} result${successCount > 1 ? 's' : ''}.`, 'success');
              }
            }
          } catch (error) {
            console.error('Error navigating to My Kits:', error);
            if (showNotification) {
              showNotification('Virtual try-on completed successfully! You can view it in My Kits.', 'success');
            }
          }
        }, 2000); // 2 second delay to show success state
      } else {
        setGenerationError('Failed to generate any virtual try-on content. Please try again.');
      }

      if (errorCount > 0 && successCount === 0) {
        setGenerationError('All virtual try-on generations failed. Please try again.');
      }
    } catch (error) {
      clearInterval(progressInterval);
      clearInterval(stepInterval);
      console.error('Error in virtual try-on generation:', error);
      setGenerationError(error.response?.data?.message || 'Failed to generate content. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Function to navigate to My Kits and refresh kits
  const handleViewInMyKits = async () => {
    try {
      // Refresh kits to get the latest data
      if (refreshKits) {
        await refreshKits();
      }
      
      // Refresh credits
      if (refreshCredits) {
        refreshCredits();
      }
      
      // Navigate to My Kits page
      if (setActiveView) {
        setActiveView('mykits');
        if (showNotification) {
          showNotification('Navigated to My Kits page. Your virtual try-on has been saved!', 'success');
        }
      }
    } catch (error) {
      console.error('Error navigating to My Kits:', error);
      if (showNotification) {
        showNotification('Failed to navigate to My Kits. Please try again.', 'error');
      }
    }
  };

  // Fetch user settings to check if virtual try-on is enabled
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await getUserSettings(token);
        setIsVirtualTryOnEnabled(response.data.data.settings?.virtualTryOnEnabled || false);
      } catch (error) {
        console.error('Error fetching user settings:', error);
        // Don't show alert, just assume disabled
        setIsVirtualTryOnEnabled(false);
      } finally {
        setIsLoadingSettings(false);
      }
    };
    fetchSettings();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 p-2 sm:p-4 lg:p-6">
      <div className="max-w-5xl mx-auto">

        {/* Loading State - Mobile Optimized */}
        {isLoadingSettings && (
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 sm:p-8 lg:p-10 border border-purple-100/50 shadow-2xl text-center mb-6 sm:mb-8">
            <Loader2 size={48} className="sm:w-16 sm:h-16 text-purple-500 mb-4 mx-auto animate-spin" />
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Loading Settings</h2>
            <p className="text-gray-600 text-base sm:text-lg">
              Checking virtual try-on availability...
            </p>
          </div>
        )}

        {/* Feature Disabled Message - Mobile Optimized */}
        {!isLoadingSettings && !isVirtualTryOnEnabled && (
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 sm:p-8 lg:p-10 border border-purple-100/50 shadow-2xl text-center mb-6 sm:mb-8">
            <Lock size={48} className="sm:w-16 sm:h-16 text-purple-500 mb-4 mx-auto" />
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Virtual Try-On Unavailable</h2>
            <p className="text-gray-600 text-base sm:text-lg mb-4 sm:mb-6">
              Virtual try-on functionality is currently disabled for your account.
              Please contact support to enable this feature.
            </p>
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 sm:p-4">
              <p className="text-purple-700 text-sm">
                <strong>Contact Support:</strong> Email support@instaad.in or call +1-800-VIRTUAL
              </p>
            </div>
          </div>
        )}

        {/* Main Content - Mobile Optimized */}
        {!isLoadingSettings && isVirtualTryOnEnabled && (
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-4 sm:p-6 lg:p-8 border border-purple-100/50 shadow-2xl">
            {/* Header - Mobile Optimized */}
            <div className="text-center mb-8 sm:mb-12">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-2xl">
                <Monitor size={32} className="sm:w-10 sm:h-10 text-white" />
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">
                Virtual Try-On
              </h1>
              <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed px-2">
                See how your products look on real people with our AI-powered virtual try-on technology
              </p>
            </div>

            {/* Pre-selected Product Display - Mobile Optimized */}
            {preSelectedProduct && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 sm:p-6 border border-purple-200 mb-6 sm:mb-8">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <img 
                    src={preSelectedProduct.image || preSelectedProduct.images?.original?.imgbbUrl || preSelectedProduct.images?.original?.url} 
                    alt={preSelectedProduct.name} 
                    className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-xl border-2 border-purple-200 shadow-lg flex-shrink-0" 
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-800 text-base sm:text-lg truncate">{preSelectedProduct.name}</h3>
                    <p className="text-purple-600 text-sm">{preSelectedProduct.category}</p>
                    <p className="text-gray-600 text-sm mt-1">Ready for virtual try-on</p>
                  </div>
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse flex-shrink-0"></div>
                </div>
              </div>
            )}

            {/* Main Action Card - Mobile Optimized */}
            <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl p-4 sm:p-6 lg:p-8 border border-purple-200 text-center">
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Ready to Try It On?</h2>
                  <p className="text-gray-600 text-sm sm:text-base">
                    Capture your photo and see how this product looks on you
                  </p>
                </div>

                {/* Simple Steps - Mobile Optimized */}
                <div className="flex justify-center space-x-4 sm:space-x-6 lg:space-x-8 mb-6 sm:mb-8">
                  <div className="flex flex-col items-center space-y-2">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-bold">1</div>
                    <span className="text-xs sm:text-sm text-gray-700 text-center">Take Photo</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 sm:w-6 lg:w-8 h-0.5 bg-purple-300"></div>
                  </div>
                  <div className="flex flex-col items-center space-y-2">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-bold">2</div>
                    <span className="text-xs sm:text-sm text-gray-700 text-center">AI Magic</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 sm:w-6 lg:w-8 h-0.5 bg-purple-300"></div>
                  </div>
                  <div className="flex flex-col items-center space-y-2">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-bold">3</div>
                    <span className="text-xs sm:text-sm text-gray-700 text-center">See Result</span>
                  </div>
                </div>

                {/* CTA Button - Mobile Optimized */}
                <button
                  onClick={openModal}
                  className="group relative w-full sm:w-auto px-8 sm:px-12 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold text-base sm:text-lg rounded-2xl transition-all duration-300 transform shadow-xl hover:from-purple-700 hover:to-pink-700 hover:scale-105 hover:shadow-2xl touch-manipulation active:scale-95"
                >
                  <span className="flex items-center justify-center">
                    <Camera size={20} className="sm:w-6 sm:h-6 mr-2 sm:mr-3 group-hover:animate-pulse" />
                    Start Virtual Try-On
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                </button>
              </div>
            </div>

            {/* Features Grid - Mobile Optimized */}
            <div className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              <div className="text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
                  <Sparkles size={20} className="sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">AI-Powered</h3>
                <p className="text-gray-600 text-xs sm:text-sm">Advanced technology for realistic results</p>
              </div>
              
              <div className="text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
                  <Zap size={20} className="sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">Lightning Fast</h3>
                <p className="text-gray-600 text-xs sm:text-sm">Generate results in seconds</p>
              </div>
              
              <div className="text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
                  <Download size={20} className="sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">High Quality</h3>
                <p className="text-gray-600 text-xs sm:text-sm">Professional-grade output</p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Full Screen Loader */}
      <FullScreenLoader 
        isVisible={isGenerating} 
        progress={loaderProgress} 
        currentStep={currentStep} 
        videoGenerationEnabled={false}
        faceSwapEnabled={false}
        currentGenerationIndex={currentGenerationIndex}
        totalGenerations={selectedProductTypes.length > 0 ? selectedProductTypes.length : 1}
      />
      
      {/* Virtual Try-On Modal - Mobile Optimized */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-100">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Virtual Try-On</h2>
              <button
                onClick={closeModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-full transition-colors touch-manipulation"
              >
                <X size={20} className="sm:w-6 sm:h-6" />
              </button>
            </div>
            <div className="p-4 sm:p-6 lg:p-8">
              {!isCameraOpen && !capturedImage && !isUploading && !imgbbUrl && (
                <div className="text-center space-y-6 sm:space-y-8">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto shadow-2xl">
                    <Camera size={28} className="sm:w-8 sm:h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3">Ready to Capture?</h3>
                    <p className="text-gray-600 text-base sm:text-lg mb-6 sm:mb-8">Take a clear photo of your face to see how the product looks on you</p>
                    {preSelectedProduct && (
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8">
                        <div className="flex items-center space-x-3 sm:space-x-4">
                          <img 
                            src={preSelectedProduct.image || preSelectedProduct.images?.original?.imgbbUrl || preSelectedProduct.images?.original?.url} 
                            alt={preSelectedProduct.name} 
                            className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-xl border-2 border-purple-200 shadow-lg flex-shrink-0" 
                          />
                          <div className="text-left min-w-0 flex-1">
                            <p className="font-semibold text-gray-800 text-base sm:text-lg truncate">{preSelectedProduct.name}</p>
                            <p className="text-purple-600 text-sm">{preSelectedProduct.category}</p>
                            <p className="text-gray-600 text-sm mt-1">Ready for virtual try-on</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={openCamera}
                    className="w-full sm:w-auto px-8 sm:px-12 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold text-base sm:text-lg rounded-2xl hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 touch-manipulation active:scale-95"
                  >
                    <span className="flex items-center justify-center">
                      <Camera size={20} className="sm:w-6 sm:h-6 mr-2 sm:mr-3" />
                      Open Camera
                    </span>
                  </button>
                </div>
              )}
              {isCameraOpen && (
                <div className="space-y-4">
                  <div className="relative">
                    <video
                      ref={handleVideoRef}
                      autoPlay
                      playsInline
                      className="w-full rounded-xl border-2 border-purple-200"
                    />
                    {/* Camera switch button positioned in top-right corner - Mobile Optimized */}
                    <button
                      onClick={switchCamera}
                      disabled={isCountingDown}
                      className="absolute top-2 right-2 sm:top-4 sm:right-4 p-2 sm:p-3 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                      title={`Switch to ${cameraFacingMode === 'user' ? 'back' : 'front'} camera`}
                    >
                      <RotateCcw size={16} className="sm:w-5 sm:h-5" />
                    </button>
                    
                    {/* Countdown overlay - Mobile Optimized */}
                    {isCountingDown && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-xl">
                        <div className="text-center">
                          <div className="text-6xl sm:text-8xl font-bold text-white mb-4 animate-pulse">
                            {countdown}
                          </div>
                          <div className="text-white text-base sm:text-lg font-medium">
                            Get ready!
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Camera Controls - Mobile Optimized */}
                  <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                    <button
                      onClick={startCountdown}
                      disabled={isCountingDown}
                      className="w-full sm:w-auto px-6 py-3 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation active:scale-95"
                    >
                      {isCountingDown ? `Capturing in ${countdown}...` : 'Capture'}
                    </button>
                    <button
                      onClick={closeCamera}
                      disabled={isCountingDown}
                      className="w-full sm:w-auto px-6 py-3 bg-gray-500 text-white font-semibold rounded-xl hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation active:scale-95"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {isUploading && (
                <div className="text-center space-y-4">
                  <Loader2 className="animate-spin text-purple-500 mx-auto" size={28} />
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800">Uploading your photo...</h3>
                  <p className="text-gray-600 text-sm sm:text-base">Please wait while we process your image</p>
                </div>
              )}
              {imgbbUrl && !selectedProduct && !preSelectedProduct && (
                <div className="space-y-4">
                  <div className="text-center">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">Photo Captured!</h3>
                    <img src={imgbbUrl} alt="Captured face" className="w-40 h-40 sm:w-48 sm:h-48 object-cover rounded-xl border-2 border-purple-200 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4 text-sm sm:text-base">Now choose a product to try on</p>
                    <button
                      onClick={() => setIsProductModalOpen(true)}
                      className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-600 transition-colors touch-manipulation active:scale-95"
                    >
                      Choose from My Products
                    </button>
                  </div>
                </div>
              )}
              {imgbbUrl && !selectedProduct && preSelectedProduct && (
                <div className="space-y-4">
                  <div className="text-center">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">Photo Captured!</h3>
                    <img src={imgbbUrl} alt="Captured face" className="w-40 h-40 sm:w-48 sm:h-48 object-cover rounded-xl border-2 border-purple-200 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4 text-sm sm:text-base">Product pre-selected from your collection</p>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4 mb-4">
                      <div className="flex items-center space-x-3">
                        <img 
                          src={preSelectedProduct.image || preSelectedProduct.images?.original?.imgbbUrl || preSelectedProduct.images?.original?.url} 
                          alt={preSelectedProduct.name} 
                          className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-lg border-2 border-purple-200 flex-shrink-0" 
                        />
                        <div className="text-left min-w-0 flex-1">
                          <p className="font-medium text-gray-800 text-sm sm:text-base truncate">{preSelectedProduct.name}</p>
                          <p className="text-sm text-gray-600">{preSelectedProduct.category}</p>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleProductSelect(preSelectedProduct)}
                      className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl hover:from-green-600 hover:to-green-700 transition-colors touch-manipulation active:scale-95"
                    >
                      Use This Product
                    </button>
                  </div>
                </div>
              )}
              {selectedProduct && (
                <div className="space-y-4">
                  <div className="text-center">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">Ready for Virtual Try-On!</h3>
                    <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 lg:space-x-8 mb-6">
                      <div className="text-center">
                        <p className="text-xs sm:text-sm text-gray-600 mb-2">Your Photo</p>
                        <img src={imgbbUrl} alt="Captured face" className="w-28 h-28 sm:w-32 sm:h-32 object-cover rounded-lg border-2 border-purple-200" />
                      </div>
                      <div className="text-center">
                        <p className="text-xs sm:text-sm text-gray-600 mb-2">Selected Product</p>
                        <img 
                          src={selectedProduct.image || selectedProduct.images?.original?.imgbbUrl || selectedProduct.images?.original?.url} 
                          alt={selectedProduct.name} 
                          className="w-28 h-28 sm:w-32 sm:h-32 object-cover rounded-lg border-2 border-purple-200" 
                        />
                        <p className="text-xs sm:text-sm font-medium text-gray-800 mt-1 truncate">{selectedProduct.name}</p>
                      </div>
                    </div>

                    {/* Product Type and Category Selection */}
                    {showProductTypeSelection && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6 mb-6">
                        <h4 className="text-base sm:text-lg font-semibold text-blue-800 mb-4 text-center">
                          Select Product Category & Types for Virtual Try-On
                        </h4>
                        <p className="text-sm text-blue-600 mb-4 text-center">
                          Choose the category and specific product types to get more accurate virtual try-on results
                        </p>
                        
                        <div className="space-y-4">
                          {/* Product Category Selection */}
                          <div>
                            <label className="block text-sm font-medium text-blue-700 mb-2">
                              Product Category
                            </label>
                            <select
                              value={selectedProductCategory}
                              onChange={(e) => handleCategoryChange(e.target.value)}
                              className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                            >
                              <option value="">Select a category...</option>
                              {PRODUCT_CATEGORIES_OPTIMIZED.map((category) => (
                                <option key={category.value} value={category.value}>
                                  {category.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Product Type Selection */}
                          {selectedProductCategory && (
                            <div>
                              <label className="block text-sm font-medium text-blue-700 mb-2">
                                Product Types
                              </label>
                              <MultiProductTypeSelector
                                selectedCategory={PRODUCT_CATEGORIES_OPTIMIZED.find(cat => cat.value === selectedProductCategory)}
                                selectedTypes={selectedProductTypes}
                                onTypesChange={handleMultipleTypesChange}
                                showTip={showMultipleTypeTip}
                                onTipToggle={() => setShowMultipleTypeTip(!showMultipleTypeTip)}
                                maxSelections={3}
                                className="virtual-try-on-selector"
                              />
                            </div>
                          )}

                          {/* Selected Types Display */}
                          {selectedProductTypes.length > 0 && (
                            <div>
                              <p className="text-sm font-medium text-blue-700 mb-2">Selected Types:</p>
                              <div className="flex flex-wrap gap-2">
                                {selectedProductTypes.map((type, index) => (
                                  <span
                                    key={index}
                                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                                  >
                                    {type.label}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 pt-4 mt-4 border-t border-blue-200">
                          <button
                            onClick={handleSkipProductTypeSelection}
                            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                          >
                            Skip (Use Default)
                          </button>
                          <button
                            onClick={handleSkipProductTypeSelection}
                            disabled={!selectedProductCategory || selectedProductTypes.length === 0}
                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                          >
                            Continue with {selectedProductTypes.length} Selected Type{selectedProductTypes.length !== 1 ? 's' : ''}
                          </button>
                        </div>
                      </div>
                    )}

                    {!isGenerating && !generatedContent && !showProductTypeSelection && (
                      <button
                        onClick={handleTryOn}
                        className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center mx-auto touch-manipulation active:scale-95"
                      >
                        <Sparkles size={18} className="mr-2" />
                        TRY-ON
                      </button>
                    )}
                  </div>
                </div>
              )}
              {generationError && (
                <div className="text-center space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                    <p className="text-red-600 font-medium text-sm sm:text-base">Generation Failed</p>
                    <p className="text-red-500 text-xs sm:text-sm">{generationError}</p>
                  </div>
                  <button
                    onClick={handleTryOn}
                    className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-600 transition-colors touch-manipulation active:scale-95"
                  >
                    Try Again
                  </button>
                </div>
              )}
              {generatedContent && (
                <div className="space-y-4 sm:space-y-6">
                  <div className="text-center">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">Virtual Try-On Complete!</h3>
                    <p className="text-gray-600 text-sm sm:text-base">
                      {generatedContents.length > 1 
                        ? `Your ${generatedContents.length} virtual try-ons have been generated successfully!`
                        : 'Your virtual try-on has been generated successfully using the specialized virtual try-on model.'
                      }
                    </p>
                  </div>
                  
                  {/* Multiple Generated Images */}
                  {generatedContents.length > 1 ? (
                    <div className="space-y-4">
                      {generatedContents.map((content, index) => (
                        <div key={index} className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 sm:p-6">
                          <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <ImageIcon size={18} className="sm:w-5 sm:h-5 mr-2 text-purple-500" />
                            {content.productType} - Virtual Try-On #{index + 1}
                          </h4>
                          <div className="relative">
                            <img 
                              src={content.generatedImageUrl} 
                              alt={`Generated content for ${content.productType}`}
                              className="w-full max-w-md mx-auto rounded-lg shadow-lg cursor-pointer hover:scale-105 transition-transform duration-200" 
                              onClick={() => openFullScreenImage(content.generatedImageUrl)}
                            />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200">
                              <div className="bg-black bg-opacity-50 text-white px-3 py-1 rounded-lg text-sm font-medium">
                                Click to view full screen
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-center mt-4">
                            <a 
                              href={content.generatedImageUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="w-full sm:w-auto px-6 py-3 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors flex items-center justify-center touch-manipulation active:scale-95"
                            >
                              <Download size={18} className="sm:w-4 sm:h-4 mr-2" />
                              Download
                            </a>
                          </div>
                          
                          {/* Kit Information for each result */}
                          {content.kit && (
                            <div className="mt-4 text-center">
                              <div className="inline-flex items-center px-3 sm:px-4 py-2 bg-green-100 text-green-800 rounded-lg">
                                <Save size={14} className="sm:w-4 sm:h-4 mr-2" />
                                <span className="text-xs sm:text-sm font-medium">
                                  ✓ Saved to kit: {content.kit.name}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Single Generated Image - Mobile Optimized */
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 sm:p-6">
                      <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <ImageIcon size={18} className="sm:w-5 sm:h-5 mr-2 text-purple-500" />
                        Generated Image
                      </h4>
                      <div className="relative">
                        <img 
                          src={generatedContent.generatedImageUrl} 
                          alt="Generated content" 
                          className="w-full max-w-md mx-auto rounded-lg shadow-lg cursor-pointer hover:scale-105 transition-transform duration-200" 
                          onClick={() => openFullScreenImage(generatedContent.generatedImageUrl)}
                        />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200">
                          <div className="bg-black bg-opacity-50 text-white px-3 py-1 rounded-lg text-sm font-medium">
                            Click to view full screen
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-center mt-4">
                        <a 
                          href={generatedContent.generatedImageUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="w-full sm:w-auto px-6 py-3 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors flex items-center justify-center touch-manipulation active:scale-95"
                        >
                          <Download size={18} className="sm:w-4 sm:h-4 mr-2" />
                          Download
                        </a>
                      </div>
                      
                      {/* Kit Information - Mobile Optimized */}
                      {generatedContent.kit && (
                        <div className="mt-4 text-center">
                          <div className="inline-flex items-center px-3 sm:px-4 py-2 bg-green-100 text-green-800 rounded-lg">
                            <Save size={14} className="sm:w-4 sm:h-4 mr-2" />
                            <span className="text-xs sm:text-sm font-medium">
                              ✓ Automatically saved to kit: {generatedContent.kit.name}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mt-2">
                            Navigating to My Kits in 2 seconds...
                          </p>
                          <div className="mt-2">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-green-500 h-2 rounded-full transition-all duration-2000" style={{ width: '100%' }}></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons - Mobile Optimized */}
                  <div className="flex justify-center mt-4 sm:mt-6">
                    {/* Try Another Product Button */}
                    <button
                      onClick={() => {
                        setSelectedProduct(null);
                        setGeneratedContent(null);
                        setGeneratedContents([]);
                        setGenerationError(null);
                        setIsProductModalOpen(true);
                      }}
                      className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-600 transition-colors flex items-center justify-center touch-manipulation active:scale-95"
                    >
                      <RotateCcw size={18} className="mr-2" />
                      Try Another Product
                    </button>
                  </div>
                </div>
              )}
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>
          </div>
        </div>
      )}
      
      {/* Enhanced Image Preview */}
      <EnhancedImagePreview
        isOpen={isFullScreenImage}
        onClose={closeFullScreenImage}
        imageUrl={fullScreenImageUrl}
        title="Virtual Try-On Result"
        showMetadata={true}
        allowDownload={true}
        allowShare={true}
        allowEdit={false}
        onDownload={(url, title) => {
          const link = document.createElement('a');
          link.href = url;
          link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.jpg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }}
        onShare={async (url, title) => {
          try {
            if (navigator.share) {
              await navigator.share({
                title: title,
                text: `Check out my virtual try-on result: ${title}`,
                url: url
              });
              // Share successful - no need to show notification
            } else {
              // Fallback to clipboard
              await navigator.clipboard.writeText(url);
              // You can add a notification here if needed
            }
          } catch (error) {
            if (error.name === 'AbortError') {
              // User cancelled share - this is normal behavior
              console.log('Share cancelled by user');
            } else {
              console.error('Share failed:', error);
              // Fallback to clipboard
              try {
                await navigator.clipboard.writeText(url);
              } catch (clipboardError) {
                console.error('Clipboard failed:', clipboardError);
              }
            }
          }
        }}
      />
      
      {/* Product Selection Modal */}
      <ProductSelectionModal
        isOpen={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        onSelect={handleProductSelect}
      />
    </div>
  );
};

export default VirtualTryView; 